import { COMPANION_RULES, DAY_MS, INDOOR_LOCATIONS, OUTDOOR_LOCATIONS, PLANT_TYPES } from '../constants';
import type { CompanionMatches, Plant, PlantStatus, TrackerSummary } from '../types';

export function isPlant(value: unknown): value is Plant {
  if (!value || typeof value !== 'object') return false;
  const plant = value as Plant;
  return Boolean(
    typeof plant.id === 'string' &&
      typeof plant.name === 'string' &&
      typeof plant.variety === 'string' &&
      (typeof plant.plant_type === 'undefined' || typeof plant.plant_type === 'string') &&
      (typeof plant.plant_count === 'undefined' || Number.isFinite(plant.plant_count)) &&
      typeof plant.chloromance_zone === 'string' &&
      Number.isFinite(plant.watering_frequency_days) &&
      Number.isFinite(plant.last_watered_timestamp)
  );
}

export function inferPlantType(plant: Plant) {
  const searchText = `${plant.name} ${plant.variety}`.toLowerCase();
  return PLANT_TYPES.find((type) => type !== 'Other' && searchText.includes(type.toLowerCase())) || 'Other';
}

export function normalizePlant(plant: Plant): Plant {
  return {
    ...plant,
    plant_type: PLANT_TYPES.includes(plant.plant_type || '') ? plant.plant_type : inferPlantType(plant),
    plant_count: getPlantCount(plant),
  };
}

export function getPlantCount(plant: Pick<Plant, 'plant_count'>) {
  return Number.isFinite(plant.plant_count) ? Math.max(1, Math.round(plant.plant_count || 1)) : 1;
}

export function createPlantId() {
  if ('crypto' in window && 'randomUUID' in window.crypto) return window.crypto.randomUUID();
  return `plant-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function formatLocationOption(location: string) {
  if (OUTDOOR_LOCATIONS.has(location)) return `${location} (Outdoor)`;
  if (INDOOR_LOCATIONS.has(location)) return `${location} (Indoor)`;
  return location;
}

export function formatPlantLocation(location: string) {
  if (OUTDOOR_LOCATIONS.has(location)) return `${location} · Outdoor`;
  if (INDOOR_LOCATIONS.has(location)) return `${location} · Indoor`;
  return location;
}

export function formatDuration(ms: number) {
  const totalHours = Math.max(1, Math.round(ms / (60 * 60 * 1000)));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  if (days > 0 && hours > 0) return `${days}d ${hours}h`;
  if (days > 0) return `${days}d`;
  return `${hours}h`;
}

export function getPlantStatus(plant: Plant, now: number, weatherMultiplier: number): PlantStatus {
  const frequencyMs = Math.max(plant.watering_frequency_days, 1) * DAY_MS;
  const elapsedMs = Math.max(0, now - plant.last_watered_timestamp);
  const effectiveElapsedMs = elapsedMs * weatherMultiplier;
  const elapsedRatio = effectiveElapsedMs / frequencyMs;
  const moisture = Math.max(0, Math.min(100, Math.round((1 - elapsedRatio) * 100)));
  const state = elapsedRatio > 1 ? 'Critical' : elapsedRatio >= 0.75 ? 'Thirsty' : 'Optimal';
  const remainingMs = (frequencyMs - effectiveElapsedMs) / weatherMultiplier;
  return {
    elapsedRatio,
    moisture,
    state,
    nextWateringText: remainingMs > 0 ? `Due in ${formatDuration(remainingMs)}` : `${formatDuration(Math.abs(remainingMs))} overdue`,
  };
}

export function buildSummary(plants: Plant[], plantStatuses: Record<string, PlantStatus>): TrackerSummary {
  return plants.reduce<TrackerSummary>(
    (summary, plant) => {
      const plantCount = getPlantCount(plant);
      const state = plantStatuses[plant.id].state;
      summary[state] += plantCount;
      summary.byZone[plant.chloromance_zone] ||= { total: 0, thirsty: 0, critical: 0 };
      summary.byZone[plant.chloromance_zone].total += plantCount;
      if (state === 'Thirsty') summary.byZone[plant.chloromance_zone].thirsty += plantCount;
      if (state === 'Critical') summary.byZone[plant.chloromance_zone].critical += plantCount;
      return summary;
    },
    { Optimal: 0, Thirsty: 0, Critical: 0, byZone: {} }
  );
}

export function calculateWiltPower(plants: Plant[], plantStatuses: Record<string, PlantStatus>) {
  if (plants.length === 0) return 100;
  const totalPlantCount = plants.reduce((total, plant) => total + getPlantCount(plant), 0);
  const totalMoisture = plants.reduce((total, plant) => total + plantStatuses[plant.id].moisture * getPlantCount(plant), 0);
  return Math.round(totalMoisture / totalPlantCount);
}

export function buildCompanionMatches(plants: Plant[]): Record<string, CompanionMatches> {
  return plants.reduce<Record<string, CompanionMatches>>((matches, plant) => {
    const rules = COMPANION_RULES[plant.plant_type || 'Other'] || COMPANION_RULES.Other;
    const zoneMates = plants.filter(
      (otherPlant) => otherPlant.id !== plant.id && otherPlant.chloromance_zone === plant.chloromance_zone
    );
    matches[plant.id] = {
      companions: zoneMates.filter((otherPlant) => rules.companions.includes(otherPlant.plant_type || 'Other')),
      antagonists: zoneMates.filter((otherPlant) => rules.antagonists.includes(otherPlant.plant_type || 'Other')),
    };
    return matches;
  }, {});
}
